/**
 * 批量任务 Store (Pinia)
 * 
 * 使用归一化存储模式：
 * - byUid: Record<string, Entity> - 字典存储，O(1) 查找
 * - allUids: string[] - 有序列表，保持顺序
 * 
 * 提供纯 Selector（computed）给组件消费
 * 
 * @version 1.0.0
 * @author Frontend Team
 * @since 2025-11-12
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { batchAPI } from '@/api/batch'
import type {
  BatchSession,
  BatchFile,
  WorkItem,
  ProcessedFile
} from '@/types/batch'
import {
  mapBatchSessionFromDTO,
  mapSessionListItemFromDTO
} from '@/types/batch.mapper'
import { isActivePhase } from '@/types/batch'

/**
 * 归一化状态结构
 */
interface NormalizedState<T> {
  byUid: Record<string, T>
  allUids: string[]
}

export const useBatchStore = defineStore('batch', () => {
  // ============= 状态定义 =============

  // 归一化存储
  const sessions = ref<NormalizedState<BatchSession>>({
    byUid: {},
    allUids: []
  })

  const files = ref<NormalizedState<BatchFile>>({
    byUid: {},
    allUids: []
  })

  const workItems = ref<NormalizedState<WorkItem>>({
    byUid: {},
    allUids: []
  })

  // 文件上传状态（用于批量转换页面）
  const uploadedFiles = ref<ProcessedFile[]>([])

  // 加载状态
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  // 当前选中的会话
  const currentSessionUid = ref<string | null>(null)

  // 分页状态
  const pagination = ref({
    currentPage: 1,
    pageSize: 20,
    total: 0
  })

  // ============= Getters (纯 Selector) =============

  /**
   * 获取所有会话列表（按创建时间倒序）
   */
  const allSessions = computed(() => {
    return sessions.value.allUids
      .map(uid => sessions.value.byUid[uid])
      .filter(Boolean)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
  })

  /**
   * 获取当前会话
   */
  const currentSession = computed(() => {
    if (!currentSessionUid.value) return null
    return sessions.value.byUid[currentSessionUid.value] ?? null
  })

  /**
   * 获取当前会话的文件列表
   */
  const currentSessionFiles = computed(() => {
    const session = currentSession.value
    if (!session || !session.files) return []
    return session.files
  })

  /**
   * 获取活跃会话列表（ready 或 running 状态）
   */
  const activeSessions = computed(() => {
    return allSessions.value.filter(s => isActivePhase(s.phase))
  })

  /**
   * 获取已完成会话列表
   */
  const completedSessions = computed(() => {
    return allSessions.value.filter(s => 
      ['completed', 'failed', 'partial', 'cancelled'].includes(s.phase)
    )
  })

  /**
   * 是否有活跃任务
   */
  const hasActiveTasks = computed(() => activeSessions.value.length > 0)

  /**
   * 活跃任务数量
   */
  const activeTaskCount = computed(() => activeSessions.value.length)

  /**
   * 根据 UID 获取会话
   */
  const getSessionByUid = (uid: string): BatchSession | null => {
    return sessions.value.byUid[uid] ?? null
  }

  /**
   * 根据 UID 获取文件
   */
  const getFileByUid = (uid: string): BatchFile | null => {
    return files.value.byUid[uid] ?? null
  }

  /**
   * 根据 UID 获取 WorkItem
   */
  const getWorkItemByUid = (uid: string): WorkItem | null => {
    return workItems.value.byUid[uid] ?? null
  }

  /**
   * 获取会话的所有文件
   */
  const getSessionFiles = (sessionUid: string): BatchFile[] => {
    const session = sessions.value.byUid[sessionUid]
    if (!session || !session.files) return []
    return session.files
  }

  /**
   * 获取文件的所有 WorkItem
   */
  const getFileWorkItems = (fileUid: string): WorkItem[] => {
    return Object.values(workItems.value.byUid).filter(item => item.fileUid === fileUid)
  }

  // ============= Actions =============

  /**
   * 添加或更新会话
   */
  const upsertSession = (session: BatchSession) => {
    const uid = session.sessionUid
    const existingSession = sessions.value.byUid[uid]

    // 如果新数据没有 files/workItems，保留旧数据（避免列表 API 覆盖详情 API 的数据）
    if (existingSession) {
      // 保留旧的 files 和 workItems（如果新数据没有）
      const mergedSession = {
        ...session,
        files: session.files ?? existingSession.files,
        workItems: session.workItems ?? existingSession.workItems
      }
      sessions.value.byUid[uid] = mergedSession
    } else {
      sessions.value.byUid[uid] = session
    }

    if (!sessions.value.allUids.includes(uid)) {
      sessions.value.allUids.push(uid)
    }

    // 同时更新关联的文件
    if (session.files) {
      session.files.forEach(file => {
        upsertFile(file)
      })
    }

    // 同时更新关联的 WorkItem
    if (session.workItems) {
      session.workItems.forEach(item => {
        upsertWorkItem(item)
      })
    }
  }

  /**
   * 更新会话进度（SSE 事件驱动）
   */
  const updateSessionProgress = (sessionUid: string, update: {
    progress: number
    completedFiles: number
    failedFiles: number
    bundleState?: string
  }) => {
    const session = sessions.value.byUid[sessionUid]
    if (session) {
      session.progress = update.progress
      session.completedFiles = update.completedFiles
      session.failedFiles = update.failedFiles
      if (update.bundleState) {
        session.bundleState = update.bundleState as any
      }
    }
  }

  /**
   * 更新会话状态（SSE 事件驱动）
   */
  const updateSessionStatus = (sessionUid: string, phase: string, extra?: {
    bundleUrl?: string
  }) => {
    const session = sessions.value.byUid[sessionUid]
    if (session) {
      session.phase = phase as any
      if (extra?.bundleUrl) {
        session.bundleUrl = extra.bundleUrl
      }
    }
  }

  /**
   * 添加或更新文件
   */
  const upsertFile = (file: BatchFile) => {
    const uid = file.fileUid
    files.value.byUid[uid] = file
    if (!files.value.allUids.includes(uid)) {
      files.value.allUids.push(uid)
    }
  }

  /**
   * 添加或更新 WorkItem
   */
  const upsertWorkItem = (item: WorkItem) => {
    const uid = item.workItemUid
    workItems.value.byUid[uid] = item
    if (!workItems.value.allUids.includes(uid)) {
      workItems.value.allUids.push(uid)
    }
  }

  /**
   * 批量添加会话
   */
  const upsertSessions = (sessionList: BatchSession[]) => {
    sessionList.forEach(session => {
      upsertSession(session)
    })
  }

  /**
   * 删除会话
   */
  const removeSession = (sessionUid: string) => {
    delete sessions.value.byUid[sessionUid]
    sessions.value.allUids = sessions.value.allUids.filter(uid => uid !== sessionUid)
  }

  /**
   * 删除文件
   */
  const removeFile = (fileUid: string) => {
    delete files.value.byUid[fileUid]
    files.value.allUids = files.value.allUids.filter(uid => uid !== fileUid)
  }

  /**
   * 添加或更新上传的文件
   */
  const upsertUploadedFile = (file: ProcessedFile) => {
    const index = uploadedFiles.value.findIndex(f => f.id === file.id)
    if (index !== -1) {
      uploadedFiles.value[index] = file
    } else {
      uploadedFiles.value.push(file)
    }
  }

  /**
   * 批量添加上传的文件
   */
  const upsertUploadedFiles = (files: ProcessedFile[]) => {
    files.forEach(file => upsertUploadedFile(file))
  }

  /**
   * 删除上传的文件
   */
  const removeUploadedFile = (id: string) => {
    const index = uploadedFiles.value.findIndex(f => f.id === id)
    if (index !== -1) {
      uploadedFiles.value.splice(index, 1)
    }
  }

  /**
   * 清空上传的文件列表
   */
  const clearUploadedFiles = () => {
    uploadedFiles.value = []
  }

  /**
   * 清空所有数据
   */
  const clearAll = () => {
    sessions.value = { byUid: {}, allUids: [] }
    files.value = { byUid: {}, allUids: [] }
    workItems.value = { byUid: {}, allUids: [] }
    uploadedFiles.value = []
    currentSessionUid.value = null
    error.value = null
  }

  /**
   * 设置当前会话
   */
  const setCurrentSession = (sessionUid: string | null) => {
    currentSessionUid.value = sessionUid
  }

  // ============= API Actions =============

  /**
   * 加载会话列表
   */
  const fetchSessions = async (page = 1, limit = 20) => {
    try {
      isLoading.value = true
      error.value = null

      // ✅ unwrap() 已自动解包，直接返回 SessionListDTO
      const data = await batchAPI.getRecentSessions(page, limit)
      const sessionList = data.sessions.map(mapSessionListItemFromDTO)
      upsertSessions(sessionList)

      // ✅ 保存分页信息
      pagination.value.currentPage = data.page
      pagination.value.pageSize = data.limit
      pagination.value.total = data.total
    } catch (err: any) {
      error.value = err.message || '加载会话列表失败'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  /**
   * 加载会话详情（包含文件）
   */
  const fetchSessionDetail = async (sessionUid: string, includeFiles = true) => {
    try {
      isLoading.value = true
      error.value = null

      // ✅ unwrap() 已自动解包，直接返回 BatchSessionDTO
      const data = await batchAPI.getSessionStatus(sessionUid, includeFiles)
      const session = mapBatchSessionFromDTO(data)
      upsertSession(session)
      return session
    } catch (err: any) {
      error.value = err.message || '加载会话详情失败'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  /**
   * 批量加载会话详情
   * @param sessionUids 会话UID列表
   * @param includeFiles 是否包含文件信息
   */
  const fetchBatchSessionDetails = async (sessionUids: string[], includeFiles = true) => {
    if (sessionUids.length === 0) {
      return
    }

    try {
      isLoading.value = true
      error.value = null

      console.log(`[BatchStore] 批量加载会话详情: ${sessionUids.length} 个任务`)

      // ✅ 调用批量接口，一次请求获取所有会话详情
      const data = await batchAPI.getBatchSessionsStatus(sessionUids, includeFiles)

      // 批量更新 store
      let updatedCount = 0
      Object.values(data).forEach(sessionDTO => {
        const session = mapBatchSessionFromDTO(sessionDTO)
        upsertSession(session)
        updatedCount++
      })

      console.log(`[BatchStore] 批量加载完成: 请求 ${sessionUids.length} 个，成功 ${updatedCount} 个`)
    } catch (err: any) {
      error.value = err.message || '批量加载会话详情失败'
      console.error('[BatchStore] 批量加载会话详情失败:', err)
      throw err
    } finally {
      isLoading.value = false
    }
  }

  /**
   * 获取进行中的会话状态（轻量级）
   * 只返回进行中的会话，不包含文件列表
   */
  const fetchActiveSessionsStatus = async () => {
    try {
      const data = await batchAPI.getActiveSessionsStatus()

      // 记录后端返回的活跃会话 UID
      const activeUids = new Set(data.sessions.map(s => s.sessionUid))

      // 更新 store 中的会话状态
      data.sessions.forEach(summary => {
        const existingSession = sessions.value.byUid[summary.sessionUid]
        if (existingSession) {
          // 只更新状态字段，不覆盖文件列表
          existingSession.phase = summary.phase as any
          existingSession.progress = summary.progress
          existingSession.completedFiles = summary.completedFiles
          existingSession.failedFiles = summary.failedFiles
          existingSession.updatedAt = summary.updatedAt
        }
      })

      // 检查本地的活跃会话，如果后端不再返回，说明已完成，需要刷新详情
      const localActiveSessions = activeSessions.value
      for (const session of localActiveSessions) {
        if (!activeUids.has(session.sessionUid)) {
          // 会话已完成，异步刷新详情（不阻塞轮询）
          console.log(`[BatchStore] 会话已完成，刷新详情: ${session.sessionUid.slice(-8)}`)
          fetchSessionDetail(session.sessionUid).catch(err => {
            console.error('[BatchStore] 刷新会话详情失败:', err)
          })
        }
      }
    } catch (err: any) {
      console.error('[BatchStore] 获取进行中的会话状态失败:', err)
      // 不抛出错误，避免影响轮询
    }
  }



  /**
   * 取消会话
   */
  const cancelSession = async (sessionUid: string) => {
    try {
      isLoading.value = true
      error.value = null

      // ✅ unwrap() 已自动解包，直接返回 void
      await batchAPI.cancelSession(sessionUid)

      // 重新加载会话详情
      await fetchSessionDetail(sessionUid, true)
    } catch (err: any) {
      error.value = err.message || '取消会话失败'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  /**
   * 判断会话是否处于最终状态
   */
  const isFinalPhase = (sessionUid: string): boolean => {
    const session = getSessionByUid(sessionUid)
    if (!session) return false
    return ['completed', 'failed', 'partial', 'cancelled'].includes(session.phase)
  }

  /**
   * 计算会话进度
   */
  const getSessionProgress = (sessionUid: string): number => {
    const session = getSessionByUid(sessionUid)
    return session?.progress ?? 0
  }

  return {
    // 状态
    sessions,
    files,
    workItems,
    uploadedFiles,
    isLoading,
    error,
    currentSessionUid,
    pagination,

    // Getters
    allSessions,
    currentSession,
    currentSessionFiles,
    activeSessions,
    completedSessions,
    hasActiveTasks,
    activeTaskCount,

    // Getter Functions
    getSessionByUid,
    getFileByUid,
    getWorkItemByUid,
    getSessionFiles,
    getFileWorkItems,

    // Actions
    upsertSession,
    updateSessionProgress,
    updateSessionStatus,
    upsertFile,
    upsertWorkItem,
    upsertSessions,
    removeSession,
    removeFile,
    upsertUploadedFile,
    upsertUploadedFiles,
    removeUploadedFile,
    clearUploadedFiles,
    clearAll,
    setCurrentSession,

    // API Actions
    fetchSessions,
    fetchSessionDetail,
    fetchBatchSessionDetails,
    fetchActiveSessionsStatus,
    cancelSession,

    // Utilities
    isFinalPhase,
    getSessionProgress
  }
})

